import { useAssetStore } from './asset'
import { useAuthStore } from './auth'
import { useCalculatorStore } from './calculator'
import { useContentStore } from './content'
import { useCrmStore } from './crm'
import { useDocumentStore } from './document'
import { useEncryptionStore } from './encryption'
import { useSessionStore } from './session'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { useSubmissionStore } from '@/stores/submission'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export type SyncOptions = {
	verify: boolean
}

export const useSyncStore = defineStore('sync', () => {
	const asset = useAssetStore()
	const calculator = useCalculatorStore()
	const content = useContentStore()
	const crm = useCrmStore()
	const document = useDocumentStore()
	const encryption = useEncryptionStore()
	const session = useSessionStore()
	const submission = useSubmissionStore()
	const cloudSyncService = useCloudSyncService()
	const authStore = useAuthStore()

	/** True if we think we're offline */
	const offline = ref<Boolean>(!navigator.onLine)

	const firstLoad = ref(true)
	const isFdl = ref(false)

	/** Map of API endpoints to their last server error (400+) */
	const serverErrors = ref<Record<string, { code: number; message?: string }>>(
		{}
	)

	/** List of active server errors */
	const activeServerErrors = computed(() =>
		Object.entries(serverErrors.value).map(([url, error]) => ({
			url,
			code: error.code,
			message: error.message,
		}))
	)

	/** Add or update a server error */
	function setServerError(url: string, code: number, message?: string) {
		serverErrors.value[url] = { code, message }
	}

	/** Clear a specific server error when it’s resolved */
	function clearServerError(url: string) {
		delete serverErrors.value[url]
	}

	/** True if a sync is in progress */
	const syncingDown = ref(false)
	const syncingUp = computed(() => cloudSyncService.hasInProgressSyncs())
	const syncing = computed(() => {
		return !offline.value && (syncingDown.value || syncingUp.value)
	})

	/** Abstract download progress for everything */
	const percentComplete = computed(() => {
		return asset.assetDownloadPercentage
			? Math.floor(asset.assetDownloadPercentage * 100)
			: undefined
	})

	// Set remote submission interval to 5 minutes (5 minutes × 60 seconds × 1000 ms = 300,000 ms)
	const remoteSubmissionErrorCheckInterval = 5 * 60 * 1000

	let remoteSubmissionErrorCheck: ReturnType<typeof setInterval> | undefined

	/**
	 * Fetches remote submission errors while the application is online.
	 */
	const checkRemoteSubmissionErrors = async () => {
		if (offline.value) {
			return
		}

		const salesforceContactAfpId = authStore.user?.salesContact?.salesforceId

		if (!salesforceContactAfpId) {
			return
		}

		await submission.fetchRemoteSubmissionErrors(salesforceContactAfpId)
	}

	/**
	 * Starts periodically checking for remote submission errors.
	 */
	const startRemoteSubmissionErrorCheck = () => {
		if (remoteSubmissionErrorCheck) {
			return
		}

		remoteSubmissionErrorCheck = setInterval(
			checkRemoteSubmissionErrors,
			remoteSubmissionErrorCheckInterval
		)
	}

	/**
	 * Stops periodically checking for remote submission errors.
	 */
	const stopRemoteSubmissionErrorCheck = () => {
		if (!remoteSubmissionErrorCheck) {
			return
		}

		clearInterval(remoteSubmissionErrorCheck)
		remoteSubmissionErrorCheck = undefined
	}

	/**
	 * Resets the periodic remote submission error check.
	 */
	const resetRemoteSubmissionErrorCheck = () => {
		stopRemoteSubmissionErrorCheck()
		startRemoteSubmissionErrorCheck()
	}

	async function beginSync(syncOptions: SyncOptions = { verify: false }) {
		if (isFdl.value) {
			beginEmbedSync(syncOptions)
		} else {
			regularSync(syncOptions)
		}
	}

	/** Start syncing everything */
	async function regularSync(syncOptions: SyncOptions = { verify: false }) {
		if (syncingDown.value) return

		syncingDown.value = true

		// Clear getAsset attempts so that assets can be reverified
		if (syncOptions.verify) {
			asset.clearAssetPromises()
		}

		try {
			// Refresh FHs first. Some later syncs depend on FH data.
			await crm.getFuneralHomesFromServer()

			const salesperson = authStore?.user?.salesContact
			const salesforceContactAfpId = salesperson?.salesforceId

			await Promise.allSettled([
				calculator.downloadDataFile(),
				crm.getAppointmentsFromServer(),
				crm.getPriceBooksFromServer(),
				crm.precacheOapProviders(),
				content.fetchDecksAndAssets(
					import.meta.env.VITE_APP_STORYBLOK_PREVIEW === 'true',
					syncOptions
				),
				content.fetchFuneralHomeGroupMedias(
					import.meta.env.VITE_APP_STORYBLOK_PREVIEW === 'true'
				),
				document.getDocumentsFromServer(syncOptions),
				encryption.fetchUpdatedServerKey(),
				session.requestQueue.retryAll(),
				submission.retryAllRequests(),
				...(salesforceContactAfpId
					? [submission.fetchRemoteSubmissionErrors(salesforceContactAfpId)]
					: []),
			])

			await cloudSyncService.retrySync()
			await cloudSyncService.plan.fetchFromServer()
			await cloudSyncService.session.fetchWalkInSessionsFromServer()
			await session.loadAll()
		} finally {
			syncingDown.value = false
			firstLoad.value = false

			// beginSync already checks remote submission errors, so restart
			// the interval from the end of the latest sync.
			resetRemoteSubmissionErrorCheck()
		}

		// Clean up
		content.deleteOldAssets()
	}

	async function beginEmbedSync(syncOptions: SyncOptions = { verify: false }) {
		if (syncingDown.value) return
		console.log('doing embed sync')
		syncingDown.value = true

		// Clear getAsset attempts so that assets can be reverified
		if (syncOptions.verify) {
			asset.clearAssetPromises()
		}

		try {
			// Refresh FHs first. Some later syncs depend on FH data.
			await crm.getFuneralHomesFromServer()

			await Promise.allSettled([
				// is this when we load their GPL
				// crm.getPriceBooksFromServer(),
				crm.precacheOapProviders(),
				// TODO: update the deck param to be tag list oriented
				content.fetchDecksAndAssets(
					import.meta.env.VITE_APP_STORYBLOK_PREVIEW === 'true',
					syncOptions,
					{ with_tag: 'fdl' }
				),
				content.fetchFuneralHomeGroupMedias(
					import.meta.env.VITE_APP_STORYBLOK_PREVIEW === 'true'
				),
				// discuss regarding plan encryption during presentation
				encryption.fetchUpdatedServerKey(),
			])
		} finally {
			syncingDown.value = false
		}

		// Clean up
		content.deleteOldAssets()
	}

	window.addEventListener('offline', () => {
		offline.value = true
		stopRemoteSubmissionErrorCheck()
	})

	window.addEventListener('online', () => {
		offline.value = false
		serverErrors.value = {}

		checkRemoteSubmissionErrors().catch()
		resetRemoteSubmissionErrorCheck()
	})

	// Turn off the firstLoad state if we detect offline status
	watch(offline, (newValue) => {
		if (newValue === true) {
			firstLoad.value = false
		}
	})

	return {
		isFdl,
		beginSync,
		startRemoteSubmissionErrorCheck,
		stopRemoteSubmissionErrorCheck,
		offline,
		serverErrors,
		activeServerErrors,
		setServerError,
		clearServerError,
		percentComplete,
		syncingDown,
		syncingUp,
		syncing,
		firstLoad,
	}
})
